import React from "react";
import {autobind} from "core-decorators";
import {Button, message, Popconfirm, Tooltip} from "antd";
import {FaTrash} from "react-icons/fa";
import {runInAction} from "mobx";
import {inject, observer} from "mobx-react";
import {gql} from "@apollo/client";

import {FinalPieceStatuses} from "@/shared/constants";
import {DeletePiece, DeletePieceVariables, PiecesQuery} from "@/graphql/types";
import Store from "@/stores";

export interface IDeletePieceActionProps {
  piece: PiecesQuery["pieces"][0];
  pieces: PiecesQuery["pieces"];
  store?: Store;
}

@inject("store")
@autobind
@observer
export default class DeletePieceAction extends React.Component<IDeletePieceActionProps> {
  async delete() {
    const {piece, pieces} = this.props;
    const pieceIdx = pieces.indexOf(piece);
    runInAction(() => pieces.splice(pieceIdx, 1)); // optimistic UI update
    try {
      const result = await this.props.store!.apollo.client.mutate<DeletePiece, DeletePieceVariables>({
        mutation: gql`
          mutation DeletePiece($pieceId: Int!) {
            piece: update_pieces_by_pk(pk_columns: {id: $pieceId}, _set: {deleted: true}) {
              id
              deleted
            }
          }
        `,
        variables: {pieceId: piece.id},
      });
      const deletedPiece = result.data?.piece;
      if (!deletedPiece) throw new Error("Could not delete Piece");
    } catch (error) {
      runInAction(() => pieces.splice(pieceIdx, 0, piece));
      console.error(error);
      message.error("Oops! Error while deleting the piece");
    }
  }

  public render() {
    const {piece, store} = this.props;
    const {session} = store!;
    if (!session.isEnsembleConductor) return null;

    if (!FinalPieceStatuses.find((status) => status === piece.status))
      return (
        <Tooltip placement="topLeft" arrowPointAtCenter title="Delete">
          <Button className="delete-btn" type="link" shape="circle" size="small" disabled>
            <FaTrash />
          </Button>
        </Tooltip>
      );

    return (
      <Tooltip placement="topLeft" arrowPointAtCenter title="Delete">
        <Popconfirm
          overlayClassName="delete-piece-popconfirm"
          title="This piece and all rehearsals associated with it will be deleted. Do you want to proceed?"
          placement="topRight"
          arrowPointAtCenter
          onConfirm={this.delete}
        >
          <Button className="delete-btn" type="link" shape="circle" size="small">
            <FaTrash />
          </Button>
        </Popconfirm>
      </Tooltip>
    );
  }
}
